/**
 * Widget-Editor (Umbau Drawer -> eigene Route, HANDOFF-2026-08-18-UX-WIDGET-EDITOR.md): Form-State
 * + debounced Auto-Save (PATCH draftConfig) + Publizieren/Pausieren/Fortsetzen/Loeschen.
 *
 * Gleiche Feld-Shape wie der bisherige Drawer-Zustand in widgets-tab.tsx (`Form`) — das Modell
 * selbst (Pflicht-/Standardfelder, freie Felder, Schluessel-Vergabe) bleibt WORTGLEICH dort stehen
 * (siehe Kommentar an `Widget` in widgets-tab.tsx: Drift-Ratsche in apps/api/test/widget-formular.
 * test.ts scannt genau diese Datei als Text). Dieser Hook importiert die Modell-Funktionen von dort,
 * statt sie zu duplizieren.
 *
 * Auto-Save-Muster uebernommen von builder.tsx (Refs fuer den aktuellen Stand, damit ein
 * debounced Save nie eine veraltete Closure speichert; kein `saveState`-Wert wird nach einem
 * Request gesetzt, wenn zwischenzeitlich ein neuerer Save begonnen hat — hier vereinfacht ohne
 * Save-Queue, weil pro Widget nur EIN Editor gleichzeitig offen ist, kein Varianten-Wechsel wie
 * im Builder).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApi, apiMutate } from "../../hooks/useApi";
import {
  BELEGTE_SCHLUESSEL,
  baueFormular,
  leseFormular,
  parseDomains,
  schluesselAus,
  type FreieZeile,
  type Widget,
} from "../widgets-tab";
import {
  baueAblauf,
  baueBedingungen,
  bereinigeBedingungen,
  folgefrageFelder,
  kreisText,
  leseAblauf,
  leseBedingungen,
  standardAblauf,
  type AblaufForm,
  type BedingungenForm,
} from "./ablauf-modell";
import { sperreZumPublizieren } from "./beratung";
import type { Aussehen } from "@maestro/widgets/aussehen";

export type SaveState = "idle" | "saving" | "saved" | "error";

export interface EditorForm {
  name: string;
  title: string;
  description: string;
  submitLabel: string;
  privacyUrl: string;
  /** W4.4 (E-W7): "an" ist der Zustand OHNE Schluessel im Entwurf (siehe baueDraftConfig unten). */
  poweredBy: boolean;
  domainsText: string;
  standardfelder: string[];
  freieFelder: FreieZeile[];
  /** Reisegruppen-Widget: Kachel-Ablauf an/aus. Aus = die Einstellungen bleiben im Editor erhalten, gespeichert wird kein `ablauf`. */
  ablaufAn: boolean;
  ablauf: AblaufForm;
  /** Punkt 129: Folgefragen (Zielfeld -> „nur zeigen, wenn Feld X einen dieser Werte hat“). */
  bedingungen: BedingungenForm;
  /** Punkt 134: Akzent, Knopf-Text, Schrift, Ecken. Nur gesetzte Werte stehen drin. */
  aussehen: Aussehen;
}

const EMPTY_FORM: EditorForm = {
  name: "",
  title: "",
  description: "",
  submitLabel: "",
  privacyUrl: "",
  poweredBy: true,
  domainsText: "",
  standardfelder: [],
  freieFelder: [],
  ablaufAn: false,
  ablauf: standardAblauf(),
  bedingungen: {},
  aussehen: {},
};

const AUTO_SAVE_DEBOUNCE_MS = 900;

function formAusWidget(w: Widget): EditorForm {
  const formular = leseFormular(w.draftConfig);
  const ablauf = leseAblauf(w.draftConfig);
  return {
    name: w.name,
    title: typeof w.draftConfig.title === "string" ? w.draftConfig.title : "",
    description: typeof w.draftConfig.description === "string" ? w.draftConfig.description : "",
    submitLabel: typeof w.draftConfig.submitLabel === "string" ? w.draftConfig.submitLabel : "",
    privacyUrl: typeof w.draftConfig.privacyUrl === "string" ? w.draftConfig.privacyUrl : "",
    poweredBy: w.draftConfig.poweredBy !== false,
    domainsText: w.allowedDomains.join("\n"),
    standardfelder: formular.standardfelder,
    freieFelder: formular.freieFelder,
    ablaufAn: ablauf !== null,
    ablauf: ablauf ?? standardAblauf(),
    bedingungen: leseBedingungen(w.draftConfig),
    aussehen: typeof w.draftConfig.aussehen === "object" && w.draftConfig.aussehen !== null ? { ...(w.draftConfig.aussehen as Aussehen) } : {},
  };
}

/** Entwurfs-Konfig aus dem Formular bauen — vorhandene fremde Schluessel (z. B. `layout` bei
 *  Bewertungs-Widgets) bleiben erhalten, nur die vom Editor gefuehrten Felder werden gesetzt. */
function baueDraftConfig(
  bestand: Record<string, unknown>,
  kind: Widget["kind"],
  f: EditorForm,
): { draftConfig: Record<string, unknown>; fehler: string[] } {
  const fehler: string[] = [];
  const draftConfig: Record<string, unknown> = { ...bestand };
  if (f.title.trim()) draftConfig.title = f.title.trim();
  else delete draftConfig.title;
  if (f.description.trim()) draftConfig.description = f.description.trim();
  else delete draftConfig.description;
  if (f.privacyUrl.trim()) draftConfig.privacyUrl = f.privacyUrl.trim();
  else delete draftConfig.privacyUrl;
  if (f.submitLabel.trim()) draftConfig.submitLabel = f.submitLabel.trim();
  else delete draftConfig.submitLabel;
  if (f.poweredBy) delete draftConfig.poweredBy;
  else draftConfig.poweredBy = false;
  if (Object.keys(f.aussehen).length > 0) draftConfig.aussehen = f.aussehen;
  else delete draftConfig.aussehen;
  if (kind === "form") {
    const formular = baueFormular(f.standardfelder, f.freieFelder);
    if (f.ablaufAn) {
      const gebaut = baueAblauf(f.ablauf);
      fehler.push(...gebaut.fehler);
      formular.ablauf = gebaut.ablauf;
    }
    // Bedingungen, deren Ziel oder Ausloeser inzwischen fehlt (Feld entfernt, Option gestrichen),
    // fallen still weg — der Server wuerde sie sonst mit 422 ablehnen.
    const felder = folgefrageFelder(f);
    const bereinigt = bereinigeBedingungen(f.bedingungen, felder);
    const bedingungen = baueBedingungen(bereinigt);
    if (bedingungen) {
      const kreis = kreisText(bereinigt, felder);
      if (kreis) fehler.push(`Die Folgefragen drehen sich im Kreis: ${kreis}. Bitte eine dieser Bedingungen entfernen.`);
      formular.bedingungen = bedingungen;
    }
    draftConfig.formular = formular;
  }
  return { draftConfig, fehler };
}

export function useWidgetEditorForm(id: string | undefined) {
  const widgetQ = useApi<Widget>(id ? `/api/widgets/${id}` : null);

  // Der lokal gefuehrte Stand nach der letzten erfolgreichen Mutation (Publish/Pause/Save) — direkt
  // aus der PATCH-Antwort uebernommen statt widgetQ.reload(): vermeidet einen zusaetzlichen
  // Roundtrip UND das kurze Lade-Flackern waehrend gerade getippt wird (H-12-Muster aus builder.tsx,
  // hier ohne Varianten-Wechsel deutlich simpler).
  const [widget, setWidget] = useState<Widget | null>(null);
  const [form, setForm] = useState<EditorForm>(EMPTY_FORM);
  const hydratedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!widgetQ.data || hydratedIdRef.current === widgetQ.data.id) return;
    hydratedIdRef.current = widgetQ.data.id;
    setWidget(widgetQ.data);
    setForm(formAusWidget(widgetQ.data));
  }, [widgetQ.data]);

  // Refs, damit der debounced Save immer den JUENGSTEN Formular-/Widget-Stand liest (nicht den zum
  // Zeitpunkt des Timer-Starts eingefangenen) — gleiches Muster wie itemsRef/daysRef in builder.tsx.
  const formRef = useRef(form);
  formRef.current = form;
  const widgetRef = useRef(widget);
  widgetRef.current = widget;

  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const runSave = useCallback(async () => {
    const bestand = widgetRef.current;
    if (!bestand) return;
    setSaveState("saving");
    try {
      const f = formRef.current;
      const gebaut = baueDraftConfig(bestand.draftConfig, bestand.kind, f);
      // Was der Betreiber selbst sofort beheben kann (Kreis in den Folgefragen, unlesbare
      // Gruppengroessen), wird VOR dem Speichern gemeldet — der Entwurf bleibt dann unveraendert.
      if (gebaut.fehler.length > 0) {
        setSaveState("error");
        setSaveError(gebaut.fehler[0] as string);
        return;
      }
      const updated = await apiMutate<Widget>(`/api/widgets/${bestand.id}`, "PATCH", {
        name: f.name.trim() || bestand.name,
        allowedDomains: parseDomains(f.domainsText),
        draftConfig: gebaut.draftConfig,
      });
      widgetRef.current = updated;
      setWidget(updated);
      setSaveState("saved");
      setSavedAt(new Date());
      setSaveError(null);
    } catch (err) {
      setSaveState("error");
      setSaveError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  // Debounced Auto-Save: jede Formular-Aenderung erhoeht `dirty`, der Effekt darunter loest nach
  // AUTO_SAVE_DEBOUNCE_MS ohne weitere Aenderung aus (Muster: builder.tsx "Debounced: bei jeder
  // Aenderung (dirty-Zaehler) nach 800 ms speichern" — hier 900 ms, siehe Bauplan "800ms-1s").
  const [dirty, setDirty] = useState(0);
  const touch = useCallback(() => setDirty((d) => d + 1), []);
  useEffect(() => {
    if (dirty === 0) return;
    const t = setTimeout(() => void runSave(), AUTO_SAVE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [dirty, runSave]);

  const updateForm = useCallback((updater: (f: EditorForm) => EditorForm) => {
    setForm(updater);
    touch();
  }, [touch]);

  /* --- Feld-Pflege (W2.1), 1:1 aus dem alten Drawer uebernommen ------------------------------ */

  const toggleStandardfeld = useCallback(
    (key: string) => {
      updateForm((f) => ({
        ...f,
        standardfelder: f.standardfelder.includes(key) ? f.standardfelder.filter((k) => k !== key) : [...f.standardfelder, key],
      }));
    },
    [updateForm],
  );

  const feldHinzufuegen = useCallback(() => {
    updateForm((f) => ({ ...f, freieFelder: [...f.freieFelder, { key: "", typ: "text", label: "", pflicht: false, keyFest: false, optionenText: "" }] }));
  }, [updateForm]);

  const feldEntfernen = useCallback(
    (i: number) => {
      updateForm((f) => ({ ...f, freieFelder: f.freieFelder.filter((_, n) => n !== i) }));
    },
    [updateForm],
  );

  const feldAendern = useCallback(
    (i: number, patch: Partial<FreieZeile>) => {
      updateForm((f) => {
        const freieFelder = f.freieFelder.map((z, n) => (n === i ? { ...z, ...patch } : z));
        const zeile = freieFelder[i]!;
        if (patch.label !== undefined && !zeile.keyFest) {
          const belegt = new Set([...BELEGTE_SCHLUESSEL, ...freieFelder.filter((_, n) => n !== i).map((z) => z.key)]);
          freieFelder[i] = { ...zeile, key: schluesselAus(patch.label, belegt) };
        }
        return { ...f, freieFelder };
      });
    },
    [updateForm],
  );

  /* --- Kachel-Ablauf und Folgefragen (Reisegruppen-Widget T5) ---------------------------------- */

  const ablaufAnAus = useCallback(
    (an: boolean) => {
      updateForm((f) => ({ ...f, ablaufAn: an }));
    },
    [updateForm],
  );

  const ablaufAendern = useCallback(
    (patch: Partial<AblaufForm>) => {
      updateForm((f) => ({ ...f, ablauf: { ...f.ablauf, ...patch } }));
    },
    [updateForm],
  );

  /** Eine Folgefrage setzen (`null` = „Immer zeigen“). */
  const bedingungSetzen = useCallback(
    (ziel: string, bedingung: { feld: string; werte: string[] } | null) => {
      updateForm((f) => {
        const bedingungen = { ...f.bedingungen };
        if (bedingung) bedingungen[ziel] = bedingung;
        else delete bedingungen[ziel];
        return { ...f, bedingungen };
      });
    },
    [updateForm],
  );

  /** Vorschau „so sieht es aus, wenn …“: Antworten, die die Live-Vorschau annimmt (nur Anzeige, wird nie gespeichert). */
  const [vorschauWerte, setVorschauWerte] = useState<Record<string, string>>({});

  // Was schon vor dem Speichern zu melden ist — dieselbe Pruefung wie beim Auto-Save.
  const fehler = useMemo(() => (widget ? baueDraftConfig(widget.draftConfig, widget.kind, form).fehler : []), [widget, form]);
  const sperre = useMemo(() => (widget?.kind === "form" ? sperreZumPublizieren(form) : null), [widget, form]);

  /* --- Aktionen: Publizieren/Pausieren/Fortsetzen/Loeschen ------------------------------------ */

  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const publish = useCallback(async () => {
    if (!widgetRef.current) return;
    setActionBusy(true);
    setActionError(null);
    try {
      const updated = await apiMutate<Widget>(`/api/widgets/${widgetRef.current.id}`, "PATCH", { publish: true });
      widgetRef.current = updated;
      setWidget(updated);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setActionBusy(false);
    }
  }, []);

  const setStatus = useCallback(async (status: "live" | "paused") => {
    if (!widgetRef.current) return;
    setActionBusy(true);
    setActionError(null);
    try {
      const updated = await apiMutate<Widget>(`/api/widgets/${widgetRef.current.id}`, "PATCH", { status });
      widgetRef.current = updated;
      setWidget(updated);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setActionBusy(false);
    }
  }, []);

  const remove = useCallback(async (): Promise<boolean> => {
    if (!widgetRef.current) return false;
    setActionBusy(true);
    setActionError(null);
    try {
      await apiMutate(`/api/widgets/${widgetRef.current.id}`, "DELETE");
      return true;
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
      return false;
    } finally {
      setActionBusy(false);
    }
  }, []);

  return {
    widget,
    form,
    loading: widgetQ.loading && !widget,
    loadError: widgetQ.error,
    updateForm,
    toggleStandardfeld,
    feldHinzufuegen,
    feldEntfernen,
    feldAendern,
    ablaufAnAus,
    ablaufAendern,
    bedingungSetzen,
    vorschauWerte,
    setVorschauWerte,
    /** Meldungen, die das Speichern verhindern (Kreis, unlesbare Gruppengroessen) — leer = alles speicherbar. */
    fehler,
    /** Rechtliche Grenze, die nur das Publizieren verhindert (fehlende Datenschutz-URL beim Kachel-Ablauf). */
    sperre,
    saveState,
    saveError,
    savedAt,
    actionBusy,
    actionError,
    publish,
    pause: () => setStatus("paused"),
    resume: () => setStatus("live"),
    remove,
  };
}

/** Rueckgabetyp von {@link useWidgetEditorForm} — von allen Schritt-Komponenten + PreviewPane als
 *  einzige Prop entgegengenommen (kein Prop-Drilling einzelner Felder). */
export type WidgetEditor = ReturnType<typeof useWidgetEditorForm>;
